import ContentProvider from 'services/content-provider';
import LocaleService from 'services/loader-handler-services/locale-service';
import StateService from 'services/state-handler-services/state-service';

// StateExportService
//
// Sends named state values out of the course.
//
// This is an export, not a store. The values it sends are already in the
// enrollment; what leaves here is a labelled copy that something outside the
// course can read without knowing how this course names things. Nothing reads
// it back.
//
// It replaces twine's `action: twine, type: saveVarsRemote`, added for
// ADO-14996 to send twine data to the platform. That action resolved
// `enrollment.twineData.x` dot-strings by hand; an activity here names a
// variable and StateService resolves where it is stored. The wire format is
// unchanged, because the receiving end is a server contract we do not own: each
// activity carries a `user_input_values` array of one entry, with the label
// already translated and the value already resolved.
//
// Only the content-service progress provider implements the call. On a homeroom
// course it fails and is reported, as it does under twine.
//
// Nothing is sent in a course build. What leaves here is a real record against
// a real learner, so a dev run must not write one.

const StateExportService = {
  // Names already reported as unproduced, so an author hears it once rather
  // than on every send.
  reported: {},

  // Turn the declared activities into the payload the progress service
  // expects. Exposed separately from `send` so a course build can show an
  // author what would have been sent, and so tests can assert on it.
  build: function (activities = []) {
    return activities
      .map(activity => {
        const name = StateService.normalizeName(activity.state);
        if (!name) {
          console.error(`StateExportService: an activity in '${activity.activity}' has no 'state' to send`);
          return undefined;
        }
        const value = StateService.get(name);
        if (value === undefined) {
          this.noteUnknown(name);
        }

        return {
          activity: activity.activity,
          activity_value: activity.activity_value,
          category: activity.category,
          user_input_values: [
            {
              input_label: LocaleService.t(activity.label),
              // An empty string, not undefined: JSON.stringify drops an
              // undefined value, and an activity that reaches the record with
              // no `input_value` key at all is harder to read than one that
              // records that the learner left it blank.
              input_value: value === undefined ? '' : value,
              // `unit` is an opaque token for the receiving end ('$', '%', '')
              // and reaches it under its original name. It is not the block's
              // `format:`, which is a display projection applied here rather
              // than a hint sent onward.
              format: activity.unit === undefined ? '' : activity.unit,
            },
          ],
        };
      })
      .filter(activity => activity !== undefined);
  },

  // A blank value is ordinary - the learner left the field alone. A name
  // nothing in the course produces is not: it is a typo that would send blank
  // forever. Only the second is worth saying, and only once.
  noteUnknown: function (name) {
    if (StateService.definitions[name] || StateService.getService(name)) {
      return;
    }
    if (this.reported[name]) {
      return;
    }
    this.reported[name] = true;
    console.error(
      `StateExportService: '${name}' is exported but nothing in this course produces it - ` +
        'it will always be sent blank'
    );
  },

  // Send one export. `config` is `{ tool, activities }`.
  send: function (config = {}) {
    if (!config.tool) {
      console.error("StateExportService: an export needs a 'tool' to file itself under");
      return undefined;
    }

    const activities = this.build(config.activities);
    if (activities.length === 0) {
      return undefined;
    }

    if (this.isLocal()) {
      // Not an error - this is the expected path in a course build. Say what
      // would have gone, so an author can check it without a real enrollment.
      console.info(`StateExportService: local build, not sending '${config.tool}'`, activities);
      return undefined;
    }

    try {
      const sent = ContentProvider.ProgressService.saveTwineVarsRemote(config.tool, activities);
      // The content-service provider returns the `fetch` promise, so a network
      // failure arrives as a rejection rather than a throw and would otherwise
      // escape this guard as an unhandled rejection.
      return sent && typeof sent.then === 'function'
        ? sent.catch(error => this.reportFailure(config.tool, error))
        : sent;
    } catch (error) {
      // A failed export must not take the page down with it. The learner's
      // answers are already in the enrollment; this is the copy that goes out.
      this.reportFailure(config.tool, error);
      return undefined;
    }
  },

  reportFailure: function (tool, error) {
    console.error(`StateExportService: could not send '${tool}': ${error && error.message}`);
    return undefined;
  },

  // `EF.options` is not there before the course boots, and a check that threw
  // while deciding whether to send would be worse than not sending.
  isLocal: function () {
    try {
      return Boolean(EF.options.local);
    } catch (error) {
      return true;
    }
  },
};

export default StateExportService;
